'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  UtensilsCrossed,
  Stethoscope,
  Building2,
  ShoppingBag,
} from 'lucide-react';
import TTOSRobotMascot from './TTOSRobotMascot';

interface TradeData {
  trade: string;
  tagline: string;
  heroOutput: string;
  stats: { label: string; value: string }[];
  deliverables: string[];
}

export const TTOSHeroV3: React.FC = () => {
  const trades: Record<string, TradeData> = {
    restaurant: {
      trade: 'Restaurant & Food',
      tagline: 'Get 40+ new table bookings every weekend on autopilot.',
      heroOutput: 'Monsoon Special Biryani Reel Script + 15% WhatsApp Promo sent to 380 diners.',
      stats: [
        { label: 'Diners Reached', value: '18.2K' },
        { label: 'WhatsApp Orders', value: '312' },
        { label: 'Google Rating', value: '4.8 ★' },
      ],
      deliverables: [
        'AI Food Reel & Menu Generator',
        'Zomato & Swiggy ROI Tracker',
        'Automated Table Reservation Bot',
      ],
    },
    doctor: {
      trade: 'Clinic & Healthcare',
      tagline: 'Book patient appointments 24/7 without a receptionist.',
      heroOutput: 'Dental Hygiene Weekly Reel + Automatic WhatsApp appointment reminders.',
      stats: [
        { label: 'Appointments Booked', value: '185' },
        { label: 'No-Show Rate', value: '3.1%' },
        { label: 'Local Rank', value: '#1 in City' },
      ],
      deliverables: [
        '24/7 Patient Booking Assistant',
        'Health Authority Compliant SEO',
        'Automated Post-Treatment Checkups',
      ],
    },
    realestate: {
      trade: 'Real Estate Developer',
      tagline: 'Close luxury property buyers directly from Instagram and WhatsApp.',
      heroOutput: 'Virtual 3BHK Video Walkthrough with instant WhatsApp brochure delivery.',
      stats: [
        { label: 'Verified Buyer Leads', value: '840' },
        { label: 'Site Visits Booked', value: '46' },
        { label: 'Ad CAC Saved', value: '₹48,000' },
      ],
      deliverables: [
        'Property Listing Landing Pages',
        'High-Intent Buyer Qualifier Bot',
        'Meta Ads Autonomous Budget Optimizer',
      ],
    },
    ecommerce: {
      trade: 'D2C Brand & Retail',
      tagline: 'Drive repeated WhatsApp checkouts with zero ad burn.',
      heroOutput: 'Festival Flash Sale Catalog + 1-click Razorpay payment link broadcast.',
      stats: [
        { label: 'Repeat Orders', value: '+64%' },
        { label: 'Abandoned Cart Recovered', value: '28%' },
        { label: 'ROAS', value: '6.4x' },
      ],
      deliverables: [
        'Automated Cart Recovery Bot',
        'Festive Product Banners & Reels',
        'Unified Inventory & GST Invoicing',
      ],
    },
  };

  const [activeTradeKey, setActiveTradeKey] = useState<string>('restaurant');
  const activeTrade = trades[activeTradeKey];

  return (
    <section className="relative pt-32 pb-20 md:pt-36 md:pb-24 overflow-hidden bg-gradient-to-b from-[#F3F4F6]/80 via-white to-white">
      {/* Radiant Background */}
      <div className="absolute top-12 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-tr from-purple-200/40 via-indigo-100/30 to-teal-100/30 blur-[130px] -z-10 rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Dynamic Trade Picker Pill Bar */}
        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex flex-wrap items-center justify-center gap-1.5 p-1.5 rounded-full bg-slate-100 border border-slate-200 shadow-inner">
            <span className="text-[11px] font-bold text-slate-500 px-2 hidden sm:inline">
              Select Your Business:
            </span>
            <button
              onClick={() => setActiveTradeKey('restaurant')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                activeTradeKey === 'restaurant'
                  ? 'bg-white text-orange-600 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <UtensilsCrossed className="w-3.5 h-3.5" />
              <span>Restaurant</span>
            </button>
            <button
              onClick={() => setActiveTradeKey('doctor')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                activeTradeKey === 'doctor'
                  ? 'bg-white text-emerald-600 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Stethoscope className="w-3.5 h-3.5" />
              <span>Clinic / Doctor</span>
            </button>
            <button
              onClick={() => setActiveTradeKey('realestate')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                activeTradeKey === 'realestate'
                  ? 'bg-white text-rose-600 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Building2 className="w-3.5 h-3.5" />
              <span>Real Estate</span>
            </button>
            <button
              onClick={() => setActiveTradeKey('ecommerce')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                activeTradeKey === 'ecommerce'
                  ? 'bg-white text-purple-600 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              <span>D2C / Retail</span>
            </button>
          </div>
        </div>

        {/* Hero Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Text */}
          <div className="lg:col-span-6 space-y-6 text-left">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
              <span>Tailored for {activeTrade.trade}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.15]">
              The Self-Driving AI Engine for{' '}
              <span className="bg-gradient-to-r from-purple-600 via-indigo-600 to-sky-600 bg-clip-text text-transparent">
                {activeTrade.trade}
              </span>
            </h1>

            <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
              {activeTrade.tagline} Switch to TT OS: our AI creates your landing page, generates your high-converting reels, and closes qualified leads on WhatsApp 24/7.
            </p>

            {/* Deliverables checklist */}
            <div className="space-y-2 pt-1">
              {activeTrade.deliverables.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-slate-700 font-medium">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-wrap items-center gap-3.5 pt-2">
              <Link
                href="/register"
                className="px-7 py-3 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-bold shadow-lg shadow-purple-500/25 transition-all hover:scale-105"
              >
                Launch {activeTrade.trade} OS
              </Link>
              <Link
                href="#simulator"
                className="px-6 py-3 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold transition-all"
              >
                Try Interactive Simulator ↓
              </Link>
            </div>
          </div>

          {/* Right Visual: Real-Time Dynamic Trade Dashboard */}
          <div className="lg:col-span-6 relative">
            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-2xl space-y-4 transition-all duration-300">
              
              {/* Card Header */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold text-xs">
                    OS
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900">{activeTrade.trade} Control Deck</h4>
                    <span className="text-[10px] text-emerald-600 font-semibold">● 100% Autonomous Mode Active</span>
                  </div>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 font-bold border border-purple-200">
                  Live Preview
                </span>
              </div>

              {/* Trade Specific Deliverable Card */}
              <div className="p-4 rounded-2xl bg-gradient-to-r from-slate-50 to-indigo-50/50 border border-slate-200 space-y-2">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span className="font-bold text-slate-700 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                    Latest Autonomous Deliverable
                  </span>
                  <span className="text-[10px] text-slate-400">Just Generated</span>
                </div>
                <p className="text-xs font-mono text-slate-800 bg-white p-3 rounded-xl border border-slate-200/80 shadow-inner">
                  &ldquo;{activeTrade.heroOutput}&rdquo;
                </p>
              </div>

              {/* 3 Metric Pills */}
              <div className="grid grid-cols-3 gap-2 text-center">
                {activeTrade.stats.map((s, idx) => (
                  <div key={idx} className="p-3 rounded-2xl bg-slate-50 border border-slate-100">
                    <span className="text-[10px] text-slate-500 block">{s.label}</span>
                    <span className="text-base font-extrabold text-indigo-700">{s.value}</span>
                  </div>
                ))}
              </div>

              {/* Mascot & Quick Launch Ribbon */}
              <div className="pt-2 flex items-center justify-between p-3 rounded-2xl bg-purple-900 text-white">
                <div className="space-y-0.5">
                  <span className="text-[10px] text-purple-300 font-semibold block uppercase">AI Manager Assistant</span>
                  <p className="text-xs font-bold text-white">Ready to deploy this workspace?</p>
                </div>
                <TTOSRobotMascot size={60} glow={false} />
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default TTOSHeroV3;
